import { useCallback, useEffect, useReducer, useState } from 'react';
import { useQuery } from 'react-query';
import { useAxios } from '../context/AxiosContext';
import {
  CustomFieldPreview,
  Template,
  TemplateDirectiveConfig,
  DefaultEntityAPI,
  EntityTypes,
  Overrides
} from '../@types/Templates';
import { TemplateConfig } from '../@types/TemplateConfigs';
import { CaseStateAction, SwitchState } from '../@types/TemplateOperators';

export function useTemplateList({ search }: { search: string }) {
  const { authAxios } = useAxios();

  const fetchTemplates = useCallback(
    async ({ searchTerm }) => {
      const response = await authAxios.get<string[]>('/reseller/templates', {
        params: {
          term: searchTerm
        }
      });
      return response.data;
    },
    [authAxios]
  );

  return useQuery<string[], Error>(['templates', search], () => fetchTemplates({ searchTerm: search }), {
    refetchOnWindowFocus: false,
    enabled: search?.length >= 3
  });
}

export function useTemplate(search: string) {
  const { authAxios } = useAxios();

  const fetchTemplate = useCallback(
    async ({ searchTerm }): Promise<Template> => {
      const response = await authAxios.get<Template>('/reseller/template', {
        params: {
          templateName: searchTerm
        }
      });
      return response.data;
    },
    [authAxios]
  );

  return useQuery<Template, Error>(['template', search], () => fetchTemplate({ searchTerm: search }), {
    refetchOnWindowFocus: false,
    enabled: search?.length >= 3
  });
}

export function useTemplateConfigs(entityType: EntityTypes, overrides: Overrides | undefined) {
  const { authAxios } = useAxios();
  const [configs, setConfigs] = useState<TemplateConfig | null>(null);

  const fetchEntityConfig = useCallback(
    async ({ entityType }) => {
      const response = await authAxios.get<TemplateConfig>('/reseller/template/config', {
        params: {
          type: entityType
        }
      });
      return response.data;
    },
    [authAxios]
  );

  const { data } = useQuery<TemplateConfig, Error>(
    ['templateConfig', entityType],
    () => fetchEntityConfig({ entityType }),
    {
      refetchOnWindowFocus: false,
      suspense: true
    }
  );

  useEffect(() => {
    if (data) {
      if (overrides?.[entityType]) {
        for (const overrideKey in overrides[entityType]) {
          if (overrides[entityType][overrideKey] === 'Y') {
            const headerExists = data.headers.some(
              (header: { objectName: string }) => header.objectName === overrideKey
            );
            if (!headerExists) {
              const newHeader = {
                displayName: overrideKey
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/([A-Z])(\d)/g, '$1 $2')
                  .replace(/([A-Z]+)(?=[A-Z][a-z])/g, '$1 '),
                objectName: overrideKey,
                canUpdate: false,
                updateDisabled: false
              };
              data.headers.push(newHeader);
            }
          } else if (overrides[entityType][overrideKey] === 'N') {
            const headerIndex = data.headers.findIndex((header) => header.objectName === overrideKey);
            if (headerIndex !== -1) {
              data.headers.splice(headerIndex, 1);
            }
          }
        }
      }
      setConfigs(data);
    }
  }, [data, entityType, overrides]);

  return { configs };
}

export const useTemplateEntityDefaults = () => {
  const { authAxios } = useAxios();

  const fetchEntityDefaults = useCallback(async () => {
    const response = await authAxios.get<DefaultEntityAPI>('/reseller/template/defaults');

    return response.data?.defaults;
  }, [authAxios]);

  return useQuery(['EntityDefaults'], () => fetchEntityDefaults(), { suspense: true, refetchOnWindowFocus: false });
};

export const useTemplateSteps = (initialValue: any) => {
  const stepsReducer = (state: TemplateDirectiveConfig[], action: any) => {
    switch (action.type) {
      case 'add':
        return [
          ...state,
          {
            operation: '',
            args: []
          }
        ];
      case 'update':
        state[action.index] = action.data;
        return [...state];
      case 'remove':
        return state.filter((value, idx) => idx !== action.index);
      default:
        return [...state];
    }
  };

  return useReducer(stepsReducer, initialValue);
};

export const useTemplateAssumedRows = (initialValue: any) => {
  const rowsReducer = (state: any[], action: any) => {
    switch (action.type) {
      case 'add':
        return [
          ...state,
          {
            rowId: action.rowId,
            AssumedRow: action.payload,
            rowValues: action.rowValues
          }
        ];
      case 'updateValue':
        return state.map((row) =>
          row.rowId === action.rowId
            ? { ...row, rowValues: { ...row.rowValues, [action.objectName]: action.value } }
            : row
        );
      case 'remove':
        return state.filter(({ rowId }) => rowId !== action.rowId);
      default:
        return [...state];
    }
  };
  return useReducer(rowsReducer, initialValue);
};

export const useTemplateStepsPreview = (
  firstDataLine: string[],
  steps: TemplateDirectiveConfig[],
  referenceRows: Partial<Record<EntityTypes, string[]>>
) => {
  const { authAxios } = useAxios();
  const fetchPreviews = useCallback(
    async ({ firstDataLine, steps }): Promise<CustomFieldPreview> => {
      const response = await authAxios.post<CustomFieldPreview>('/reseller/transform/preview', {
        row: firstDataLine || [],
        steps,
        referenceRows
      });
      return response.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authAxios]
  );
  return useQuery(['templateStepsPreview', { firstDataLine, steps }], () => fetchPreviews({ firstDataLine, steps }));
};

export const useTemplateCopy = () => {
  const { authAxios } = useAxios();

  return useCallback(
    async (newTemplateName: string, previousTemplateName: string, rename: boolean = false): Promise<number> => {
      const response = await authAxios.get('/reseller/template/copy', {
        params: { newTemplateName, previousTemplateName, rename }
      });
      return response.status;
    },
    [authAxios]
  );
};

export function useTemplateName({ search = '' }: { search: string }) {
  const { authAxios } = useAxios();

  const checkTemplateNameAvailability = useCallback(
    async (name) => {
      const { data } = await authAxios.get('/reseller/template/validate-name', {
        params: { name }
      });
      return data.availability;
    },
    [authAxios]
  );

  return useQuery(['templateNameCheck', search], () => checkTemplateNameAvailability(search), {
    refetchOnWindowFocus: false,
    initialData: false,
    enabled: search.length >= 3
  });
}

export const useSwitchConfig = (initialValue: any) => {
  const emptyCase = {
    compare: [
      {
        compareTo: '',
        operator: ''
      }
    ],
    returns: ''
  };

  const defaultState: SwitchState = {
    cases: [emptyCase],
    default: ''
  };

  function reducer(state: SwitchState, action: CaseStateAction): SwitchState {
    switch (action.type) {
      case 'addCase':
        return { ...state, cases: [...state.cases, emptyCase] };
      case 'setCompareTo':
        if (action.idx !== undefined && action.val !== undefined) {
          state.cases[action.idx].compare[0].compareTo = action.val.toString();
        }
        return { ...state };
      case 'setOperator':
        if (action.idx !== undefined && action.val !== undefined) {
          state.cases[action.idx].compare[0].operator = action.val.toString();
        }
        return { ...state };
      case 'setReturns':
        if (action.idx !== undefined && action.val !== undefined) {
          state.cases[action.idx].returns = action.val;
        }
        return { ...state };
      case 'setDefault':
        if (action.val !== undefined) {
          state.default = action.val;
        }
        return { ...state };
      default:
        throw new Error();
    }
  }

  return useReducer(reducer, initialValue || defaultState);
};
